// =============================================================================
// ProjectDefectsTab.tsx — the Defects tab on a project (9 Oct 2026)
//
// James, 9 Oct 2026: "I think we should have a project specific defect screen
// in projects that show the defects on that site and the nav button can have a
// qty and colour on it green = 0, Amber is Open with ETA and RED Open with no
// ETA or past due ETA" — and "let the field view so they can see the defect on
// their sites when they are curious".
//
// One file, three exports:
//   * useProjectDefectBadge(projectId) — open count + colour for the tab button
//   * <DefectCountBadge/>              — the little coloured number
//   * default <ProjectDefectsTab/>     — the tab body
//
// It reads the SAME table the Defect Register does (site_defects), filtered to
// this project. Nothing new in the database: the read policy already lets a
// manager see every defect and anyone ON the project see that project's.
//   * Manager+ : raise a defect here (project pre-filled), open one to edit
//     who goes back / ETA / status — the Register's own modals, reused.
//   * Everyone else: read-only. Tap a defect to unfold the detail.
//
// The colour rule lives in defectTone() and nowhere else:
//   green  — nothing open
//   amber  — open, and every open one has an ETA that is still ahead
//   red    — any open one with no ETA, or past its ETA
// "Open" = anything not closed and not cancelled (in progress and awaiting
// re-inspection still count — somebody still has to go back or sign it off).
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Wrench, Plus, CheckCircle2, Clock, CalendarClock, ClipboardList,
  User as UserIcon, ChevronDown, ChevronRight, Timer,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Pill, KPI, Spinner, EmptyState, Button } from "@/components/ui";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import RaiseDefectModal from "./RaiseDefectModal";
import DefectDetailModal from "./DefectDetailModal";

type Status = "open" | "in_progress" | "awaiting_reinspection" | "closed" | "cancelled";
const STATUS_LABEL: Record<Status, string> = {
  open: "Open", in_progress: "In progress", awaiting_reinspection: "Awaiting re-inspection", closed: "Closed", cancelled: "Cancelled",
};
const STATUS_TONE: Record<Status, any> = {
  open: "critical", in_progress: "pending", awaiting_reinspection: "notice", closed: "approved", cancelled: "default",
};
const SEV_TONE: Record<string, any> = { minor: "notice", major: "warning", critical: "critical" };

// Local day key — never toISOString (it shifts the day in AEST).
function localKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const isOpen = (d: any) => d.status !== "closed" && d.status !== "cancelled";
// due_date is a plain date ("2026-10-14"); due TODAY is not late yet.
const isPastEta = (d: any) => isOpen(d) && !!d.due_date && String(d.due_date) < localKey(new Date());
const needsAttention = (d: any) => isOpen(d) && (!d.due_date || isPastEta(d));

export type DefectTone = "ok" | "warn" | "danger";
export interface DefectBadge { loaded: boolean; open: number; attention: number; tone: DefectTone }

export function defectTone(defects: any[]): DefectBadge {
  const open = defects.filter(isOpen);
  const attention = open.filter(needsAttention).length;
  return {
    loaded: true,
    open: open.length,
    attention,
    tone: open.length === 0 ? "ok" : attention > 0 ? "danger" : "warn",
  };
}

function useProjectDefects(projectId: string | undefined) {
  return useQuery({
    queryKey: ["project_defects", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("site_defects").select(`
        defect_id, defect_number, category, severity, title, description, status,
        due_date, raised_at, closed_at, source, client_contact,
        pack:pack_id ( pack_number ),
        assignee:assigned_to_employee_id ( first_name, last_name )
      `).eq("project_id", projectId).order("raised_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
}

/** Open count + colour for the tab button. Shares its fetch with the tab. */
export function useProjectDefectBadge(projectId: string | undefined): DefectBadge {
  const { data } = useProjectDefects(projectId);
  return useMemo(
    () => (data ? defectTone(data) : { loaded: false, open: 0, attention: 0, tone: "ok" as DefectTone }),
    [data],
  );
}

export function DefectCountBadge({ badge }: { badge: DefectBadge }) {
  if (!badge.loaded) return null;
  const cls =
    badge.tone === "ok"   ? "bg-ok/15 text-ok border-ok/40" :
    badge.tone === "warn" ? "bg-warn/15 text-warn border-warn/40" :
                            "bg-danger/15 text-danger border-danger/40";
  const title =
    badge.tone === "ok"   ? "No open defects" :
    badge.tone === "warn" ? `${badge.open} open — each has an ETA still ahead` :
                            `${badge.open} open — ${badge.attention} with no ETA or past it`;
  return (
    <span title={title}
      className={`ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full border text-[10px] font-mono font-semibold leading-none ${cls}`}>
      {badge.open}
    </span>
  );
}

export default function ProjectDefectsTab({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  const [view, setView] = useState<"open" | "all" | "closed">("open");
  const [raise, setRaise] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);        // manager: the edit modal
  const [unfolded, setUnfolded] = useState<string | null>(null);    // everyone else: read-only detail
  const { data: defects = [], isLoading, refetch } = useProjectDefects(projectId);

  const kpi = useMemo(() => {
    const badge = defectTone(defects);
    const closed = defects.filter(d => d.status === "closed" && d.closed_at);
    const avg = closed.length
      ? Math.round(closed.reduce((a, d) => a + Math.max(0, differenceInCalendarDays(new Date(d.closed_at), new Date(d.raised_at))), 0) / closed.length)
      : null;
    const oldest = defects.filter(isOpen).reduce((m, d) => Math.max(m, differenceInCalendarDays(new Date(), new Date(d.raised_at))), 0);
    return { ...badge, closed: closed.length, avg, oldest };
  }, [defects]);

  const shown = useMemo(() => {
    if (view === "all") return defects;
    if (view === "closed") return defects.filter(d => !isOpen(d));
    return defects.filter(isOpen);
  }, [defects, view]);

  if (isLoading) return <div className="py-12 flex justify-center"><Spinner size={20} /></div>;

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[15px] font-semibold flex items-center gap-2"><Wrench size={16} className="text-accent" /> Defects on this project</div>
          <p className="text-text-muted text-[12px] mt-0.5">
            Things we have to go back for on this site — who is on it, the ETA and where it is up to. Tracked beside the job; a defect never blocks the project.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && <Link to="/acceptance/defects" className="text-[12px] text-text-muted hover:text-accent">Defect Register (all sites)</Link>}
          {canManage && <Button size="sm" icon={Plus} onClick={() => setRaise(true)}>Raise defect</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Open" value={kpi.open} icon={ClipboardList} accent={kpi.open > 0}
             sub={kpi.open > 0 ? `oldest ${kpi.oldest} day${kpi.oldest === 1 ? "" : "s"}` : "nothing to go back for"} />
        <KPI label="No ETA / past ETA" value={kpi.attention} icon={CalendarClock} accent={kpi.attention > 0}
             sub={kpi.attention > 0 ? "needs a date or a push" : "all on track"} />
        <KPI label="Closed" value={kpi.closed} icon={CheckCircle2} sub="on this project" />
        <KPI label="Avg days to close" value={kpi.avg == null ? "—" : kpi.avg} icon={Timer} sub={kpi.avg == null ? "none closed yet" : "raised → closed"} />
      </div>

      <div className="mb-3 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md w-fit">
        {(["open", "all", "closed"] as const).map(t => (
          <button key={t} onClick={() => setView(t)}
            className={`px-3 py-1.5 rounded text-[12px] capitalize ${view === t ? "bg-accent/15 text-accent border border-accent/30" : "text-text-muted hover:text-text border border-transparent"}`}>
            {t}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Card>
          <EmptyState icon={CheckCircle2}
            title={view === "open" ? "No open defects" : "Nothing here"}
            description={view === "open"
              ? (defects.length ? "Clean — everything raised on this project is closed." : "Nothing has been raised on this project.")
              : "No defects match this filter."} />
        </Card>
      ) : (
        <div className="space-y-2">
          {shown.map(d => {
            const late = isPastEta(d);
            const noEta = isOpen(d) && !d.due_date;
            const days = d.closed_at
              ? differenceInCalendarDays(new Date(d.closed_at), new Date(d.raised_at))
              : differenceInCalendarDays(new Date(), new Date(d.raised_at));
            const isUnfolded = unfolded === d.defect_id;
            return (
              <Card key={d.defect_id}>
                <button type="button" className="w-full text-left p-4"
                  onClick={() => canManage ? setOpenId(d.defect_id) : setUnfolded(isUnfolded ? null : d.defect_id)}>
                  <div className="flex items-start gap-2">
                    <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${!isOpen(d) ? "bg-ok" : (late || noEta) ? "bg-danger" : "bg-warn"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[12px] text-accent">{d.defect_number}</span>
                        <Pill label={STATUS_LABEL[d.status as Status] ?? d.status} tone={STATUS_TONE[d.status as Status] ?? "default"} small />
                        <Pill label={d.severity} tone={SEV_TONE[d.severity] ?? "default"} small />
                        <span className="text-[11px] text-text-muted capitalize">{d.category}</span>
                        {d.source === "client" && <Pill label="client-raised" tone="bid" small />}
                        {late && <span className="text-[11px] text-danger font-medium">· past ETA</span>}
                        {noEta && <span className="text-[11px] text-danger font-medium">· no ETA</span>}
                      </div>
                      <div className="text-[14px] text-text mt-1">{d.title || d.description}</div>
                      {d.title && !isUnfolded && <div className="text-[12px] text-text-muted mt-0.5 line-clamp-2">{d.description}</div>}
                      <div className="text-[11px] text-text-muted mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="inline-flex items-center gap-1"><UserIcon size={11} /> {d.assignee ? `${d.assignee.first_name} ${d.assignee.last_name}` : "Nobody assigned"}</span>
                        {d.due_date && <span className={`inline-flex items-center gap-1 ${late ? "text-danger" : ""}`}><Clock size={11} /> ETA {format(parseISO(d.due_date), "dd MMM")}</span>}
                        <span>raised {format(parseISO(d.raised_at), "dd MMM")}</span>
                        <span>{d.closed_at ? `closed in ${days}d` : isOpen(d) ? `${days}d open` : ""}</span>
                        {d.pack?.pack_number && <span>{d.pack.pack_number}</span>}
                      </div>
                      {!canManage && isUnfolded && (
                        <div className="mt-3 pt-3 border-t border-line text-[12px] text-text-muted space-y-1.5">
                          <div className="text-[13px] text-text whitespace-pre-wrap">{d.description}</div>
                          {d.client_contact && <div>Reported by: {d.client_contact}</div>}
                          <div>Raised {format(parseISO(d.raised_at), "dd MMM yyyy")}{d.closed_at ? ` · closed ${format(parseISO(d.closed_at), "dd MMM yyyy")}` : ""}</div>
                          <div className="text-text-dim">Something here not right, or already fixed? Tell your supervisor — the office updates the defect.</div>
                        </div>
                      )}
                    </div>
                    {!canManage && (isUnfolded ? <ChevronDown size={14} className="text-text-dim mt-1" /> : <ChevronRight size={14} className="text-text-dim mt-1" />)}
                  </div>
                </button>
              </Card>
            );
          })}
        </div>
      )}

      {raise && <RaiseDefectModal projectId={projectId} onClose={() => setRaise(false)} onCreated={() => refetch()} />}
      {openId && <DefectDetailModal defectId={openId} onClose={() => setOpenId(null)} onChanged={() => refetch()} />}
    </div>
  );
}
